import React from 'react';
import { ConfigGroup } from '../Mfp/PivotConfigurator/PivotConfigurator.types';
import { VisualizeSummaryPivotValueProps } from 'src/pages/Mfp/MfpGrid/visualize-summary-pivot-ag.types';
import { getCellStateFromCells } from 'src/pivot/PivotCells.utils';
import { GridAsyncState, GRID_DEFAULT } from 'src/state/scope/Scope.types';
import { createDimensionList } from 'src/utils/Component/visualize';
import { Tooltip, Button, CircularProgress, IconButton } from '@material-ui/core';

export interface VisualizeContextMenuProps extends Pick<VisualizeSummaryPivotValueProps, 'hasEditableRevision'> {
  cellState: ReturnType<typeof getCellStateFromCells>;
  gridAsyncState: GridAsyncState;
  availableGroup: ConfigGroup[];
  favoritesLoading: boolean;
  handleClickUndo: () => void;
  handleClickRedo: () => void;
  handleClickLayoutManagerOpen: () => void;
  handleClickDownload: () => void;
  freezeCells: () => void;
  lockCells: () => void;
  relaxCells: () => void;
}

/**
 * Conditionally renders the icons above and to the right of the grid
 * Return order is important, as it determines the order the icons are rendered in
 */
export const VisualizeContextMenu = (props: VisualizeContextMenuProps): JSX.Element => {
  const {
    cellState,
    hasEditableRevision,
    relaxCells,
    lockCells,
    freezeCells,
    favoritesLoading,
    handleClickDownload,
    handleClickLayoutManagerOpen,
    handleClickRedo,
    handleClickUndo,
    availableGroup,
    gridAsyncState,
  } = props;

  // Initializes the context menu with the text that shows the current dimensions
  // This is always rendered to the left
  const dimensionListText = (
    <div key="dimension-list" className="left-item">
      <span>Reviewing: </span>
      {createDimensionList(availableGroup)}
      <div className="dim-item" key="saving">
        {gridAsyncState !== GRID_DEFAULT ? `(${gridAsyncState})` : null}
      </div>
    </div>
  );

  const contextItems: React.ReactNode[] = [dimensionListText];
  if (cellState.isUnlockable && hasEditableRevision) {
    contextItems.push(
      <Tooltip key="unconstrain" title="Unlock selected cell(s)" className="tooltip-for-icon">
        <Button data-qa="unlock-btn-unlock" className={'top-align-btn'}
          style={{ minWidth: 'unset' }} onClick={relaxCells}>
          <i className="far fa-unlock" />
        </Button>
      </Tooltip>
    );
  }
  if (cellState.isUnFreezeable && hasEditableRevision) {
    contextItems.push(
      <Tooltip key="unfreeze" title="Unfreeze selected cell(s)" className="tooltip-for-icon">
        <Button data-qa="unfreeze-btn-unfreeze" className={'top-align-btn'}
          style={{ minWidth: 'unset' }} onClick={relaxCells}>
          <i className="far fa-fire-alt" />
        </Button>
      </Tooltip>
    );
  }

  if (cellState.isRelaxed && hasEditableRevision) {
    contextItems.push(
      <Tooltip key="lock" title="Lock selected cell(s)" className="tooltip-for-icon">
        <Button data-qa="lock-btn-lock" className={'top-align-btn'}
          style={{ minWidth: 'unset' }} onClick={lockCells}>
          <i className="far fa-lock" />
        </Button>
      </Tooltip>
    );
    contextItems.push(
      <Tooltip key="freeze" title="Freeze selected cell(s)" className="tooltip-for-icon">
        <Button data-qa="freeze-btn-freeze" className={'top-align-btn'}
          style={{ minWidth: 'unset' }} onClick={freezeCells}>
          <i className="far fa-snowflake" />
        </Button>
      </Tooltip>
    );
  }

  if (hasEditableRevision) {
    contextItems.push(
      <Button
        key="undo"
        data-qa="undo-btn-undo"
        className={'top-align-btn'}
        onClick={handleClickUndo}
        startIcon={<i className="far fa-undo-alt" />}
      >
        Undo
      </Button>
    );
    contextItems.push(
      <Button
        key="redo"
        data-qa="redo-btn-redo"
        className={'top-align-btn'}
        onClick={handleClickRedo}
        startIcon={<i className="far fa-redo-alt" />}
      >
        Redo
      </Button>
    );
  }

  contextItems.push(
    <Button
      key="configure"
      data-qa="configure-btn-open"
      className={'top-align-btn'}
      onClick={handleClickLayoutManagerOpen}
      startIcon={<i className="far fa-cog" />}
    >
      Configure
    </Button>
  );

  contextItems.push(
    <Tooltip key="popup_download" className="tooltip-for-icon" title="Download data as XLSX">
      <span>
        <Button
          data-qa="down-btn-download"
          className="down-btn-download"
          onClick={handleClickDownload}
          disabled={favoritesLoading}
        >
          {favoritesLoading ? <CircularProgress size={20} /> : <i className="far fa-cloud-download" />}
        </Button>
      </span>
    </Tooltip>
  );

  return <React.Fragment>{contextItems}</React.Fragment>;
};
